import { useMemo, memo } from 'react';
import { Bar } from 'react-chartjs-2';
import { styled } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Typography } from '@components/Typography';
import { LANGUAGE } from '@constants/language';
import { formatPercentNumber } from '@helpers/formatNumbers';
import { useTranslation } from '@hooks';

const Container = styled('div')({
    display: 'flex',
    '@media (max-width: 600px)': {
        display: 'block',
        width: '100%',
        position: 'relative',
        marginTop: '16px',
    },
});

const TextWrapper = styled('div')({
    paddingTop: '14px',
    '@media (max-width: 600px)': {
        width: '100%',
        position: 'absolute',
        paddingTop: 0,
        left: '6px',
        top: '5px',
    },
});

const Label = styled(Typography)({
    display: 'block',
    marginBottom: '6px',
    lineHeight: '16px !important',
    color: 'rgba(0, 0, 0, 0.87)',
    '&:not(:first-child)': {
        marginBottom: '5px',
    },
    '@media (max-width: 600px)': {
        marginBottom: '20px',
        '&:not(:first-child)': {
            marginBottom: '20px',
        },
    },
    '@media (max-width: 345px)': {
        fontSize: '0.65rem !important',
    },
});

const ConversionType = {
    Surveys: 0,
    Qr: 1,
    Total: 2,
    Mock: 3,
};

const createChartData = (data, maxValue, isLoading) => {
    const Colors = {
        [ConversionType.Surveys]: '#1F4C5C',
        [ConversionType.Qr]: '#BDE0CC',
    };
    return {
        labels: ['', '', '', ''],
        datasets: [
            ...Object.entries(data).map(([key, item]) => ({
                label: key,
                data: !isLoading ? [item.openedSurveys, item.responsesReceived, item.leaveReviewClicked, 0] : [],
                percent: !isLoading
                    ? [item.openedSurveysPercent, item.responsesReceivedPercent, item.leaveReviewClickedPercent, 0]
                    : [],
                backgroundColor: [Colors[key], Colors[key], Colors[key], 'rgba(239, 244, 203, 1)'],
                hoverBackgroundColor: [Colors[key], Colors[key], Colors[key], 'rgba(239, 244, 203, 1)'],
                maxBarThickness: 16,
                categoryPercentage: 1.0,
                barPercentage: 1.0,
                borderWidth: 0,
            })),
            {
                label: ConversionType.Total,
                data: !isLoading ? [0, 0, 0, data[ConversionType.Surveys]?.reviewsReceived || 0] : [],
                percent: !isLoading ? [0, 0, 0, data[ConversionType.Surveys]?.reviewsReceivedPercent || 0] : [],
                backgroundColor: 'rgba(239, 244, 203, 1)',
                hoverBackgroundColor: 'rgba(239, 244, 203, 1)',
                maxBarThickness: 16,
                categoryPercentage: 1.0,
                barPercentage: 1.0,
                borderWidth: 0,
            },
            {
                label: ConversionType.Mock,
                data:
                    data[ConversionType.Surveys].openedSurveys || data[ConversionType.Qr].openedSurveys || isLoading
                        ? Array(4).fill(maxValue)
                        : Array(4).fill(10),
                percent: Array(4).fill(0),
                backgroundColor: '#E4EAEB',
                hoverBackgroundColor: '#E4EAEB',
                maxBarThickness: 16,
                categoryPercentage: 1.0,
                barPercentage: 1.0,
                borderWidth: 0,
            },
        ],
    };
};

const createChartOptions = (t, maxValue) => {
    const TypeMap = {
        [ConversionType.Surveys]: t('AnalyticsConversion.surveyInvitations'),
        [ConversionType.Qr]: t('AnalyticsConversion.qrCodes'),
        [ConversionType.Total]: t('AnalyticsConversion.reviewsReceived'),
        [ConversionType.Mock]: '',
    };
    return {
        indexAxis: 'y',
        elements: {
            bar: {
                borderWidth: 0,
            },
        },
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: false,
            },
            title: {
                display: false,
            },
            tooltip: {
                displayColors: false,
                enabled: true,
                backgroundColor: 'rgba(0, 0, 0, 1)',
                filter: function (context) {
                    const allowedDatasetIndexes = context.dataIndex === 3 ? [2] : [0, 1];
                    return allowedDatasetIndexes.includes(context.datasetIndex);
                },
                callbacks: {
                    title: () => null,
                    label: function (context) {
                        return `${TypeMap[context.datasetIndex]} - ${context.formattedValue} (${formatPercentNumber(
                            context.dataset.percent[context.dataIndex],
                        )})`;
                    },
                },
            },
        },
        scales: {
            x: {
                display: false,
                stacked: true,
                max: maxValue || 10,
                grid: {
                    tickLength: 0,
                },
            },
            y: {
                grid: {
                    display: false,
                    drawBorder: false,
                    tickLength: 0,
                },
                border: {
                    display: false,
                },
                stacked: true,
                max: maxValue > 10 ? maxValue : 10,
            },
        },
    };
};

const ConversionBarChart = memo(({ data, language, isLoading }) => {
    const { t } = useTranslation();
    const isMobile = useMediaQuery('@media (max-width: 600px)');

    const dataMap = { [ConversionType.Surveys]: data.surveys, [ConversionType.Qr]: data.qr };

    const labelsMap = useMemo(
        () => [
            {
                label: t('AnalyticsConversion.openedSurveys'),
                value: data.openedSurveys,
                percent: data.openedSurveysPercentage,
            },
            {
                label: t('AnalyticsConversion.responsesReceived'),
                value: data.responsesReceived,
                percent: data.responsesReceivedPercentage,
            },
            {
                label: t('AnalyticsConversion.proceededToReviewSite'),
                value: data.leaveReviewClicked,
                percent: data.leaveReviewClickedPercentage,
            },
            {
                label: t('AnalyticsConversion.reviewsReceived'),
                value: data.reviewsReceived,
                percent: data.reviewsReceivedPercentage,
            },
        ],
        [t, data],
    );

    const max = useMemo(() => labelsMap.reduce((acc, { value }) => Math.max(acc, value), 0), [labelsMap]);

    const datasets = useMemo(() => createChartData(dataMap, max, isLoading), [dataMap, max, isLoading]);

    const options = useMemo(() => createChartOptions(t, max), [max, t]);

    const TextWrapperWidth = {
        [LANGUAGE.ENGLISH]: 230,
        [LANGUAGE.UKRAINIAN]: 240,
        [LANGUAGE.PORTUGUESE]: 240,
        [LANGUAGE.SPANISH]: 240,
    };

    return (
        <Container>
            <TextWrapper style={{ width: `${TextWrapperWidth[language]}px` }}>
                {labelsMap.map(({ label, value, percent }) => (
                    <Label key={label} variant="caption">
                        {label} - {value} ({formatPercentNumber(percent)})
                    </Label>
                ))}
            </TextWrapper>

            <div
                style={{
                    position: 'relative',
                    width: isMobile ? '100%' : `calc(100% - ${TextWrapperWidth[language]}px)`,
                    height: isMobile ? '165px' : '106px',
                }}
            >
                <Bar data={datasets} options={options} />
            </div>
        </Container>
    );
});

ConversionBarChart.displayName = 'ConversionBarChart';

export default ConversionBarChart;
